<template>
	<div class="ks-videoeditor-screen-wrap">
		<div class="ks-videoeditor-screen" ref="screen" @click="screen_click">
			<!-- Layers paint in DOM order: clips < overlays < frame box -->
			<div class="ks-videoeditor-layer">
				<video
					v-for="clip in video_clips"
					:key="clip.id"
					ref="videos"
					:data-id="clip.id"
					:src="preview_src(clip)"
					class="ks-videoeditor-video"
					:style="clip_frame_style(clip)"
					playsinline
					preload="auto"
					@loadedmetadata="video_loaded(clip, $event)"></video>
				<img
					v-for="clip in image_clips"
					:key="'img' + clip.id"
					ref="images"
					:data-id="clip.id"
					:src="clip.url"
					class="ks-videoeditor-video"
					:style="clip_frame_style(clip)"
					draggable="false"
					alt=""
					@load="image_loaded(clip, $event)">
			</div>
			<div class="ks-videoeditor-layer">
				<img
					v-for="o in overlays"
					:key="'ov' + o.id"
					ref="overlay_imgs"
					:data-id="o.id"
					:src="o.url"
					class="ks-videoeditor-overlay-img"
					:style="overlay_style(o)"
					draggable="false"
					alt=""
					@load="image_loaded(o, $event)">
			</div>
			<frame-box v-if="selected_frame" :item="selected_frame" @change="frame_change" @pick="$emit('pick', $event)"></frame-box>
			<div v-if="timeline.length == 0 && overlays.length == 0 && audios.length == 0" class="ks-videoeditor-screen-empty">Add clips from the sources</div>
		</div>
		<audio
			v-for="a in audios"
			:key="'au' + a.id"
			ref="audios"
			:data-id="a.id"
			:src="a.url"
			preload="auto"
			@loadedmetadata="$emit('loaded')"></audio>
	</div>
</template>

<script>
import FrameBox from './FrameBox.vue'
import {overlay_height} from './frame.js'
import {fade_factor, preview_url, set_style} from './utils.js'

// Renders the media elements of the timeline and keeps them in step with the playback time via sync()
export default {
	components: {FrameBox},
	props: {
		timeline: Array,
		overlays: Array,
		audios: Array,
		layout: Array,
		hidden_ids: Object,
		muted_ids: Object,
		track_z_map: Object,
		selected_frame: Object,
		preview_low: Boolean
	},
	created () {
		this.last_seek = {};
	},
	computed: {
		video_clips () {
			return _.filter(this.timeline, (c) => c.type != "image");
		},
		image_clips () {
			return _.filter(this.timeline, (c) => c.type == "image");
		}
	},
	methods: {
		preview_src (clip) {
			return preview_url(clip, this.preview_low);
		},
		clip_frame_style (clip) {
			return {left: clip.x + "%", top: clip.y + "%", width: clip.w + "%", height: overlay_height(clip) + "%"};
		},
		overlay_style (o) {
			return {zIndex: this.track_z_map[o.track], left: o.x + "%", top: o.y + "%", width: o.w + "%", height: "auto"};
		},
		video_loaded (clip, event) {
			const v = event.target;
			if (v.videoWidth && v.videoHeight) this.$emit("aspect", clip, v.videoWidth / v.videoHeight);
			this.$emit("loaded");
		},
		image_loaded (item, event) {
			const img = event.target;
			if (img.naturalWidth && img.naturalHeight) this.$emit("aspect", item, img.naturalWidth / img.naturalHeight);
		},
		frame_change (item, patch) {
			this.$emit("update", item, patch);
		},
		// Click position in % of the frame
		screen_click (event) {
			const rect = this.$refs.screen.getBoundingClientRect();
			this.$emit("pick", {
				x: (event.clientX - rect.left) / rect.width * 100,
				y: (event.clientY - rect.top) / rect.height * 100
			});
		},

		apply_layer_styles (styles, visible_overlays) {
			_.each([].concat(this.$refs.videos || [], this.$refs.images || []), (el) => {
				const s = styles[el.dataset.id];
				set_style(el, "opacity", s ? s.opacity : 0);
				set_style(el, "zIndex", s ? s.zIndex : 0);
			});
			_.each(this.$refs.overlay_imgs || [], (el) => {
				set_style(el, "opacity", visible_overlays[el.dataset.id] || 0);
			});
		},
		// While playing, a media element that is still loading lags behind; seeking it again every frame would keep it from ever playing
		seek_media (el, key, target, playing) {
			const drift = Math.abs(el.currentTime - target);
			if (playing) {
				if (drift <= 0.5 || el.seeking) return;
				const now = performance.now();
				if (now - (this.last_seek[key] || 0) < 1000) return;
				this.last_seek[key] = now;
			} else if (drift <= 0.03) {
				return;
			}
			el.currentTime = target;
		},
		// trim: {clip, side} while an edge of a clip is dragged, then only the frame at that edge is shown
		sync (t, playing, trim) {
			const videos = _.keyBy(this.$refs.videos || [], (v) => v.dataset.id);
			const styles = {};
			const active = {};
			const visible_overlays = {};
			_.each(this.overlays, (o) => {
				if (!this.hidden_ids[o.track] && t >= o.start && t < o.start + o.duration) {
					visible_overlays[o.id] = fade_factor(o, o.start, o.start + o.duration, t) * o.opacity / 100;
				}
			});

			if (trim) {
				const clip = trim.clip;
				const edge = trim.side == "in" ? clip.in : Math.max(clip.in, clip.out - 0.05);
				_.each(videos, (v, id) => {
					if (!v.paused) v.pause();
					if (id == clip.id && Math.abs(v.currentTime - edge) > 0.01) v.currentTime = edge;
				});
				styles[clip.id] = {opacity: 1, zIndex: 1};
				this.apply_layer_styles(styles, visible_overlays);
				this.sync_audios(t, playing, true);
				return;
			}

			_.each(this.layout, (l) => {
				if (t < l.start || t >= l.end) return;
				if (this.hidden_ids[l.track]) return;
				const v = videos[l.clip.id];
				if (!v && l.clip.type != "image") return;
				active[l.clip.id] = true;

				if (v) {
					const local = l.clip.in + (t - l.start);
					this.seek_media(v, "v" + l.clip.id, local, playing);
					if (playing && v.paused) v.play().catch(() => {});
					if (!playing && !v.paused) v.pause();
				}

				const tr = l.clip.transition;
				let fade_in = 1;
				if (tr.type == "fade" && tr.duration > 0) {
					fade_in = _.clamp((t - l.start) / (l.xfade ? l.xfade.length : tr.duration), 0, 1);
				}
				const fade_out = l.fade_out > 0 ? _.clamp((l.end - t) / l.fade_out, 0, 1) : 1;
				styles[l.clip.id] = {opacity: fade_in * fade_out * l.clip.opacity / 100, zIndex: this.track_z_map[l.track] + l.order + 1};
			});

			// Inactive clips stay paused at their in point so the first frame is ready
			_.each(this.timeline, (clip) => {
				const v = videos[clip.id];
				if (!v) return;
				const volume = _.clamp(clip.volume / 100, 0, 1);
				const muted = !!clip.muted || !!this.muted_ids[clip.track];
				if (v.volume !== volume) v.volume = volume;
				if (v.muted !== muted) v.muted = muted;
				if (active[clip.id]) return;
				if (!v.paused) v.pause();
				if (v.readyState > 0 && Math.abs(v.currentTime - clip.in) > 0.03) v.currentTime = clip.in;
			});
			this.apply_layer_styles(styles, visible_overlays);
			this.sync_audios(t, playing, false);
		},
		sync_audios (t, playing, mute) {
			const players = _.keyBy(this.$refs.audios || [], (el) => el.dataset.id);
			_.each(this.audios, (a) => {
				const el = players[a.id];
				if (!el) return;
				el.volume = _.clamp(a.volume / 100, 0, 1);
				const active = !mute && !this.muted_ids[a.track] && t >= a.start && t < a.start + a.duration;
				if (!active) {
					if (!el.paused) el.pause();
					if (el.readyState > 0 && Math.abs(el.currentTime - a.in) > 0.03) el.currentTime = a.in;
					return;
				}
				const local = a.in + (t - a.start);
				this.seek_media(el, "a" + a.id, local, playing);
				if (playing && el.paused) el.play().catch(() => {});
				if (!playing && !el.paused) el.pause();
			});
		}
	}
}
</script>

<style scoped>
.ks-videoeditor-screen-wrap { flex: 1 1 0; min-height: 0; container-type: size; display: flex; align-items: center; justify-content: center; }
.ks-videoeditor-screen { position: relative; width: min(100%, calc(100% * 16 / 9)); aspect-ratio: 16 / 9; background: #000; overflow: hidden; border-radius: 4px; }
.ks-videoeditor-layer { position: absolute; inset: 0; isolation: isolate; pointer-events: none; }
.ks-videoeditor-video { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: contain; background: #000; opacity: 0; }
.ks-videoeditor-overlay-img { position: absolute; pointer-events: none; opacity: 0; }
.ks-videoeditor-screen-empty { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; color: #888; }
</style>
